import { XMarkIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useState } from 'react';
import { RetainerSelector, OUTSIDE_ODO_ID } from '@odo/shared-ui';
import Spinner from '../Spinner';

type Step = 'confirm' | 'retainers';

interface EndTreatmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (retainerOption: string, reason?: string) => void;
    isSubmitting?: boolean;
}

const EndTreatmentModal: React.FC<EndTreatmentModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isSubmitting = false
}) => {
    const [step, setStep] = useState<Step>('confirm');
    const [selectedRetainer, setSelectedRetainer] = useState<string | null>(null);
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setStep('confirm');
            setSelectedRetainer(null);
            setDisclaimerAccepted(false);
            setReason('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const isOutsideOdo = selectedRetainer === OUTSIDE_ODO_ID;
    const canSubmit = selectedRetainer !== null && (!isOutsideOdo || disclaimerAccepted);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        {step === 'confirm' ? 'End treatment' : 'Select your retainers'}
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className={`${isSubmitting ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-800'}`}
                        aria-label="Close"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 pb-4 space-y-5 overflow-y-auto scrollbar-hide">
                    {step === 'confirm' && (
                        <div className="flex flex-col items-center gap-4 py-4">
                            <p className="text-sm text-gray-600 text-center">
                                Are you sure you want to end this patient&apos;s treatment?
                            </p>
                            <p className="text-sm text-gray-600 text-center">
                                This action will put the patient in retention.
                            </p>
                        </div>
                    )}

                    {step === 'retainers' && (
                        <>
                            <p className="text-sm text-gray-600">
                                Select your retainer option to complete the process.
                            </p>

                            <RetainerSelector
                                selectedRetainer={selectedRetainer}
                                onSelect={setSelectedRetainer}
                                disclaimerAccepted={disclaimerAccepted}
                                onDisclaimerChange={setDisclaimerAccepted}
                            />

                            <div className="space-y-2">
                                <p className="text-sm text-gray-500">
                                    Anything we should know? (optional)
                                </p>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl placeholder-gray-400/60 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 transition-colors duration-200 resize-none h-36"
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                    {step === 'confirm' && (
                        <>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-semibold rounded-lg border text-gray-800 bg-white border-gray-300 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => setStep('retainers')}
                                className="px-6 py-2.5 text-sm font-bold rounded-lg bg-red-600 text-white hover:bg-red-700"
                            >
                                Yes, end treatment
                            </button>
                        </>
                    )}

                    {step === 'retainers' && (
                        <>
                            <button
                                onClick={() => setStep('confirm')}
                                disabled={isSubmitting}
                                className={`px-4 py-2 text-sm font-semibold rounded-lg border ${
                                    isSubmitting
                                        ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                                        : 'text-gray-800 bg-white border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                Back
                            </button>
                            <button
                                onClick={() =>
                                    selectedRetainer &&
                                    onSubmit(
                                        selectedRetainer,
                                        reason.trim() ? reason.trim() : undefined
                                    )
                                }
                                disabled={!canSubmit || isSubmitting}
                                className={`px-6 py-2.5 text-sm font-bold rounded-lg flex items-center gap-2 ${
                                    canSubmit && !isSubmitting
                                        ? 'bg-gray-800 text-white hover:bg-gray-700'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                {isSubmitting && <Spinner />}
                                Submit
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EndTreatmentModal;
